import { Component, Input, OnInit } from '@angular/core'
import {
  AttachmentService,
  DataCheckerService,
  notAllNumbersValidator,
  selectAllUsers,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { createAutomationDateForm } from '@tv3/containers/automations/automation-data-form/date-form/automation-date-form.component'
import * as moment from 'moment/moment'
import { select, Store } from '@ngrx/store'
import { AuthService } from '@tv3/services/auth.service'
import {
  AutomationDate,
  AutomationDateRuleTypes,
  AutomationEventTypes,
  isValidAutomationDatePair,
} from '@tokeet-frontend/automations'
import { distinctUntilChanged, map, startWith } from 'rxjs/operators'
import { combineLatest, Observable } from 'rxjs'
import * as lodash from 'lodash'
import { AutomationAttachmentsDataBaseComponent } from '@tv3/containers/automations/automation-data-form/automation-attachments-data-base.component'
import { getTaskStatusOptions, selectTaskLists, TaskGuard, TaskStatus } from '@tokeet-frontend/tasks'

const defaultTaskStart: AutomationDate = { base: AutomationDateRuleTypes.AfterBookingCreated, days: 0, hours: 2 }
const defaultTaskDue: AutomationDate = { base: AutomationDateRuleTypes.AfterBookingCreated, days: 0, hours: 3 }

export function createAutomationTaskDataForm(fb: FormBuilder, isStartDateRequired = true) {
  return fb.group({
    name: ['', [Validators.required, notAllNumbersValidator]],
    list: [''],
    user_ids: [[], [Validators.required]],
    start: createAutomationDateForm(fb, defaultTaskStart, {
      baseRequired: isStartDateRequired,
      daysRequired: isStartDateRequired,
      hoursRequired: isStartDateRequired,
    }),
    due: createAutomationDateForm(fb, defaultTaskDue, {
      baseRequired: false,
      daysRequired: false,
      hoursRequired: false,
    }),
    status: [TaskStatus.Pending, [Validators.required]],
    notes: ['', [Validators.required]],
    tags: [[]],
    attachments: [[]],
    inquiry_id: [],
    timezone: [moment.tz.guess()],
  })
}

@Component({
  selector: 'app-automation-task-data-form',
  templateUrl: './automation-task-data-form.component.html',
  styleUrls: ['./automation-task-data-form.component.scss'],
})
export class AutomationTaskDataFormComponent extends AutomationAttachmentsDataBaseComponent implements OnInit {
  @Input() form: FormGroup
  @Input() eventCtrl: FormControl
  @Input() hideDateFields = false

  get attachmentsCtrl(): FormControl {
    return this.form.get('attachments') as FormControl
  }
  statuses = getTaskStatusOptions(false)

  users$ = this.store.pipe(select(selectAllUsers))
  filteredLists$: Observable<string[]>

  constructor(
    protected store: Store<any>,
    protected authService: AuthService,
    protected attachmentService: AttachmentService,
    private dataCheckerService: DataCheckerService
  ) {
    super(store, authService, attachmentService)
    this.dataCheckerService.check([TaskGuard])
  }

  ngOnInit(): void {
    super.ngOnInit()

    this.filteredLists$ = combineLatest([
      this.store.pipe(select(selectTaskLists)),
      this.form.get('list').valueChanges.pipe(startWith('')),
    ]).pipe(map(([lists, value]) => lists.filter((list) => list.toLowerCase().includes((value || '').toLowerCase()))))

    const startCtrl = this.form.get('start') as FormGroup
    const endCtrl = this.form.get('due') as FormGroup

    startCtrl.addValidators([
      (c) => {
        const data = c.value
        if (!isValidAutomationDatePair(data, endCtrl.getRawValue())) {
          return { invalid: true }
        }
      },
    ])
    endCtrl.addValidators([
      (c) => {
        const data = c.value
        if (!isValidAutomationDatePair(startCtrl.getRawValue(), data)) {
          return { invalid: true }
        }
      },
    ])
    endCtrl.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => lodash.isEqual(a, b)),
        untilDestroy(this)
      )
      .subscribe(() => {
        startCtrl.updateValueAndValidity()
      })
    startCtrl.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => lodash.isEqual(a, b)),
        untilDestroy(this)
      )
      .subscribe(() => {
        endCtrl.updateValueAndValidity()
      })

    this.eventCtrl?.valueChanges.pipe(untilDestroy(this)).subscribe((event) => {
      startCtrl.reset(defaultTaskStart)
      endCtrl.reset(defaultTaskDue)
    })
  }

  isBookingConfirmedOnBookingCreatedEvent(event: AutomationEventTypes, base: AutomationDateRuleTypes) {
    return (
      [AutomationEventTypes.InquiryCreated, AutomationEventTypes.InvoiceCreated].includes(event) &&
      base === AutomationDateRuleTypes.AfterBookingConfirmed
    )
  }
}
